/**
 * A customer organization's own people, managed by its own administrator.
 *
 * The counterpart to `/admin/organizations`, which is MindPeer staff handing a
 * tenant to its first administrator. Everyone that administrator adds after
 * that is added here, with no platform role involved.
 *
 * It lives in the auth portal rather than in the product for the same
 * mechanical reason the staff panel does: these endpoints authenticate with
 * the auth session cookie, which is `SameSite=Lax` and so is not sent on a
 * cross-origin request from a module. The product links here (`/portal/people`
 * in core) instead of duplicating the surface.
 *
 * Functional only — no design pass (Thanasis, 7 Sep).
 */
import { useCallback, useEffect, useState } from 'react';
import type { InvitationSummary, MembershipSummary, OrganizationRole } from '@mindpeer/auth-shared';
import { Alert, Button, Card, CardHeader, Field, Input, Spinner } from '@/components/ui';
import { useAuth } from '@/hooks/use-auth';
import { ApiError, api } from '@/lib/api';

/**
 * What each role may hand out — the same rule the server enforces, mirrored
 * here so the form does not offer a choice that will come back a 403. The
 * server remains the authority; this is courtesy, not a check.
 */
const OWNER_MAY_GRANT: readonly OrganizationRole[] = ['owner', 'admin', 'member', 'viewer'];
const ADMIN_MAY_GRANT: readonly OrganizationRole[] = ['admin', 'member', 'viewer'];

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

export function OrganizationPeoplePage() {
  const { user } = useAuth();
  const organization = user?.activeOrganization ?? null;

  const [membership, setMembership] = useState<MembershipSummary | null | undefined>(undefined);
  const [invitations, setInvitations] = useState<InvitationSummary[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<string>('member');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [lastAcceptUrl, setLastAcceptUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // The authority to invite comes from the caller's membership, so it is read
  // from the membership rather than inferred from anything on the session.
  const loadMembership = useCallback(async () => {
    try {
      const { memberships } = await api.memberships();
      setMembership(memberships.find((row) => row.isActive) ?? null);
    } catch {
      setMembership(null);
    }
  }, []);

  const loadInvitations = useCallback(async () => {
    try {
      const { invitations: rows } = await api.myOrganizationInvitations();
      setInvitations(rows);
      setLoadError(null);
    } catch (error) {
      // A 403 here is not a failure to report: it is the ordinary answer for a
      // member who is not an administrator, and the page says so on its own.
      if (error instanceof ApiError && (error.status === 403 || error.status === 400)) return;
      setLoadError(errorMessage(error, 'Could not load pending invitations.'));
    }
  }, []);

  useEffect(() => {
    document.title = 'People | MindPeer Auth';
    void loadMembership();
  }, [loadMembership]);

  const administers =
    !!membership && (membership.roles.includes('owner') || membership.roles.includes('admin'));

  useEffect(() => {
    if (administers) void loadInvitations();
  }, [administers, loadInvitations]);

  const grantable = membership?.roles.includes('owner') ? OWNER_MAY_GRANT : ADMIN_MAY_GRANT;

  const invite = async () => {
    setInviting(true);
    setInviteError(null);
    setCopied(false);
    try {
      const { invitation } = await api.createMyOrganizationInvitation({
        email,
        roles: [role],
      });
      // Surfaced for the same reason as in the staff panel: an environment
      // with no mail transport reports a successful invitation that the
      // invitee never hears about.
      setLastAcceptUrl(invitation.acceptUrl ?? null);
      setEmail('');
      await loadInvitations();
    } catch (error) {
      setInviteError(errorMessage(error, 'Could not send the invitation.'));
    } finally {
      setInviting(false);
    }
  };

  const revoke = async (invitationId: string) => {
    try {
      await api.revokeMyOrganizationInvitation(invitationId);
      await loadInvitations();
    } catch (error) {
      setInviteError(errorMessage(error, 'Could not revoke the invitation.'));
    }
  };

  if (membership === undefined) {
    return (
      <div className="flex justify-center py-12" role="status">
        <Spinner className="size-6 text-brand-600" />
        <span className="sr-only">Loading</span>
      </div>
    );
  }

  if (!organization) {
    return (
      <Card>
        <CardHeader title="People" description="You are not a member of an organization yet." />
      </Card>
    );
  }

  if (!administers) {
    return (
      <Card>
        <CardHeader
          title={`People at ${organization.name}`}
          description="Only an administrator of this organization can invite people to it."
        />
        <Alert tone="info">
          Ask an administrator of <strong>{organization.name}</strong> to invite someone.
        </Alert>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title={`Invite to ${organization.name}`}
          description="They will be emailed a link. It expires, and works only for the address it was sent to."
        />
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            void invite();
          }}
        >
          {inviteError && <Alert tone="error">{inviteError}</Alert>}
          <Field label="Email address" htmlFor="invite-email">
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="colleague@example.com"
              required
            />
          </Field>
          <Field
            label="Role"
            htmlFor="invite-role"
            hint="Administrators can invite other people and manage this organization."
          >
            <select
              id="invite-role"
              value={role}
              onChange={(event) => setRole(event.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              {grantable.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </Field>
          <Button type="submit" loading={inviting} disabled={!email.trim()}>
            Send invitation
          </Button>
        </form>

        {lastAcceptUrl && (
          <Alert tone="success">
            <p className="mb-2">
              Invitation sent. If mail is not configured in this environment, share this link
              directly:
            </p>
            <code className="block break-all text-xs">{lastAcceptUrl}</code>
            <Button
              variant="secondary"
              onClick={() => {
                void navigator.clipboard.writeText(lastAcceptUrl).then(() => setCopied(true));
              }}
            >
              {copied ? 'Copied' : 'Copy link'}
            </Button>
          </Alert>
        )}
      </Card>

      <Card>
        <CardHeader title="Pending invitations" description="Not yet accepted." />
        {loadError && <Alert tone="error">{loadError}</Alert>}
        {invitations.length === 0 ? (
          <p className="text-sm text-slate-500">No pending invitations.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Role</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {invitations.map((invitation) => (
                  <tr key={invitation.id} className="border-b border-slate-100">
                    <td className="py-2 pr-4">{invitation.email}</td>
                    <td className="py-2 pr-4 text-slate-500">{invitation.roles.join(', ')}</td>
                    <td className="py-2 pr-4 text-slate-500">{invitation.status}</td>
                    <td className="py-2">
                      {invitation.status === 'pending' && (
                        <Button variant="secondary" onClick={() => void revoke(invitation.id)}>
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
